/**
 * @fileoverview Invitation Management Page
 *
 * Purpose: View, create, revoke, and resend platform invitations.
 * URL: /admin/invitations
 * Protection: Requires super_admin role
 *
 * Features:
 * - Tab bar: All | Pending | Accepted | Revoked
 * - Data table with invitation details
 * - Invite User dialog
 * - Row actions: Revoke, Resend (pending only)
 *
 * Dependencies:
 * - Convex: admin.invitations.list, revoke, resend
 * - shadcn Table, Tabs, Badge, Button, AlertDialog
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation, useAction } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { Plus, RotateCw, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { InviteUserDialog } from "./-ui.invite-user-dialog";

export const Route = createFileRoute("/_app/_auth/admin/_layout/invitations")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: InvitationsPage,
});

// ---------------------------------------------------------------------------
// Status badge styling
// ---------------------------------------------------------------------------

const STATUS_BADGE_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  accepted: "default",
  revoked: "destructive",
  expired: "secondary",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  accepted: "Accepted",
  revoked: "Revoked",
  expired: "Expired",
};

// ---------------------------------------------------------------------------
// Tab options
// ---------------------------------------------------------------------------

type StatusFilter = "all" | "pending" | "accepted" | "revoked";

const TABS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "Revoked", value: "revoked" },
];

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function InvitationsPage() {
  const [activeTab, setActiveTab] = useState<StatusFilter>("all");

  const statusArg =
    activeTab === "all"
      ? undefined
      : (activeTab as "pending" | "accepted" | "revoked");

  const { data: invitations } = useQuery(
    convexQuery(api.admin.invitations.list, {
      status: statusArg,
    }),
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Invitations</h1>
          <p className="text-sm text-muted-foreground">
            Manage platform access invitations.
          </p>
        </div>
        <InviteUserDialog>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Invite User
          </Button>
        </InviteUserDialog>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border p-1">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === tab.value
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Invited By</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Accepted</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invitations && invitations.length > 0 ? (
              invitations.map((inv: InvitationRowProps["invitation"]) => (
                <InvitationRow key={inv._id} invitation={inv} />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-muted-foreground"
                >
                  {invitations === undefined
                    ? "Loading..."
                    : "No invitations found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invitation Row
// ---------------------------------------------------------------------------

interface InvitationRowProps {
  invitation: {
    _id: Id<"platformInvitations">;
    email: string;
    inviterName?: string;
    organisationName?: string;
    status: string;
    createdAt: number;
    acceptedAt?: number;
  };
}

function InvitationRow({ invitation }: InvitationRowProps) {
  const revokeMutation = useMutation(api.admin.invitations.revoke);
  const resendAction = useAction(api.admin.invitations.resend);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleRevoke = async () => {
    setIsRevoking(true);
    try {
      await revokeMutation({ invitationId: invitation._id });
      toast.success("Invitation revoked.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to revoke.");
    } finally {
      setIsRevoking(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await resendAction({ invitationId: invitation._id });
      toast.success("Invitation email resent.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to resend.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{invitation.email}</TableCell>
      <TableCell>{invitation.inviterName ?? "—"}</TableCell>
      <TableCell>{invitation.organisationName ?? "—"}</TableCell>
      <TableCell>
        <Badge variant={STATUS_BADGE_VARIANT[invitation.status] ?? "outline"}>
          {STATUS_LABELS[invitation.status] ?? invitation.status}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatRelativeDate(invitation.createdAt)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {invitation.acceptedAt
          ? formatRelativeDate(invitation.acceptedAt)
          : "—"}
      </TableCell>
      <TableCell>
        {invitation.status === "pending" && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={isResending}
              title="Resend invitation email"
            >
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isRevoking}
                  title="Revoke invitation"
                >
                  <XCircle className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to revoke the invitation for{" "}
                    <strong>{invitation.email}</strong>? They will no longer be
                    able to sign up with this invitation.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRevoke}>
                    Revoke
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a timestamp as a relative date string (e.g. "3 days ago").
 */
function formatRelativeDate(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 30) {
    return new Date(timestamp).toLocaleDateString();
  }
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMinutes > 0) return `${diffMinutes}m ago`;
  return "Just now";
}
